import React, { ReactElement, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import pl from 'translations/pl.json';
import { PaymentPartial } from 'types/Payment';

import { PaymentNotificationContainer, PaymentNotificationBold } from './PaymentNotification.styles';
import {
  getDefaultPaymentNotificationSettings,
  NotificationProperties,
  disabledPaymentMethodNotification,
  unsupportedApplePayPaymentMethodNotification,
} from './NotificationProperties';
import withSelectedMethodContext from '../../hoc/withSelectedMethodContext';
import { ISelectedMethodContext } from '../../types/SelectedMethodContext';
import { DisableReason, PaymentCategoryType } from '../../consts';

interface IPaymentNotification extends RouteComponentProps, Partial<ISelectedMethodContext> {
  progress: number;
  payment: PaymentPartial;
  paymentMethodType: string | undefined;
  showPaymentNotification: boolean | undefined;
}

function isNotificationVisibleForCurrentProgress(progress: number, newNotificationSettings: NotificationProperties) {
  return progress >= newNotificationSettings.fromProgress && progress <= newNotificationSettings.toProgress;
}

function isUnsupportedApplePay(payment: PaymentPartial, selectedPaymentMethodDisabledReason: string) {
  return (
    PaymentCategoryType.APPLE_PAY === payment.selectedPaymentMethodType &&
    DisableReason.UNSUPPORTED === selectedPaymentMethodDisabledReason
  );
}

function getChosenSelectedPaymentMethodDisabledNotification(
  payment: PaymentPartial,
  selectedPaymentMethodDisabledReason: string
) {
  return isUnsupportedApplePay(payment, selectedPaymentMethodDisabledReason)
    ? unsupportedApplePayPaymentMethodNotification
    : disabledPaymentMethodNotification;
}

function PaymentNotification(props: IPaymentNotification): ReactElement {
  const {
    progress,
    payment,
    paymentMethodType,
    showPaymentNotification,
    isSelectedPaymentMethodDisabled,
    selectedPaymentMethodDisabledReason,
  } = props;
  const [notificationProperties, setNotificationProperties] = useState<NotificationProperties>();
  const [notificationVisible, setNotificationVisible] = useState<boolean>(false);

  useEffect(() => {
    const shouldDisplayDisabledPaymentMethodNotification =
      isSelectedPaymentMethodDisabled === true && !paymentMethodType;

    const shouldDisplayNotificationForLevelZeroPayment =
      isSelectedPaymentMethodDisabled === false && payment.selectedShowPaymentNotification;

    let isNotificationVisible = false;
    let newNotificationSettings: NotificationProperties | undefined;

    if (shouldDisplayDisabledPaymentMethodNotification) {
      newNotificationSettings = getChosenSelectedPaymentMethodDisabledNotification(
        payment,
        selectedPaymentMethodDisabledReason
      );
    } else if (shouldDisplayNotificationForLevelZeroPayment || showPaymentNotification) {
      const finalPaymentMethodType = paymentMethodType || payment.selectedPaymentMethodType || 'NO_PAYMENT_METHOD';
      const isLevelZero = payment.selectedPaymentMethodType != null;
      newNotificationSettings = getDefaultPaymentNotificationSettings(finalPaymentMethodType, isLevelZero);
    }

    if (newNotificationSettings !== undefined) {
      isNotificationVisible = isNotificationVisibleForCurrentProgress(progress, newNotificationSettings);
      setNotificationProperties(newNotificationSettings);
    }

    setNotificationVisible(isNotificationVisible);
  }, [
    progress,
    paymentMethodType,
    payment,
    showPaymentNotification,
    isSelectedPaymentMethodDisabled,
    selectedPaymentMethodDisabledReason,
  ]);

  return (
    <>
      <PaymentNotificationContainer isVisible={notificationVisible} className="payment-notification__container">
        {notificationProperties && (
          <>
            {notificationProperties.notification && (
              <p>
                <FormattedMessage
                  id={notificationProperties.notification}
                  defaultMessage={pl[notificationProperties.notification]}
                />
              </p>
            )}
            {notificationProperties.notificationBold && (
              <PaymentNotificationBold className="statusbar__paymentNotificationBold">
                <FormattedMessage
                  id={notificationProperties.notificationBold}
                  defaultMessage={pl[notificationProperties.notificationBold]}
                />
              </PaymentNotificationBold>
            )}
          </>
        )}
      </PaymentNotificationContainer>
    </>
  );
}

export default withSelectedMethodContext(withRouter(PaymentNotification));
